import { useCallback, useEffect, useRef, useState } from 'react';
import { subDays, startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import {
  AnalyticsDashboardData,
  analyticsDashboardService,
  AnalyticsReprocessPeriodResult,
  analyticsReprocessPeriodService,
} from '../services/firebaseFunctions';

// Converte Date para string 'YYYY-MM-DD' no fuso local
const toLocalDateString = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

const getLast30Days = (): { start: Date; end: Date } => {
  const today = new Date();
  return { start: subDays(today, 29), end: today };
};

export function useAnalyticsDashboard() {
  const defaults = getLast30Days();

  const [data, setData] = useState<AnalyticsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date>(defaults.start);
  const [endDate, setEndDate] = useState<Date>(defaults.end);

  // Guard to avoid duplicate in-flight requests
  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async (start: Date, end: Date) => {
    const currentId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const result = await analyticsDashboardService.getData({
        startDate: toLocalDateString(start),
        endDate: toLocalDateString(end),
      });

      if (fetchIdRef.current !== currentId) return; // stale response
      setData(result);
    } catch (err: any) {
      if (fetchIdRef.current !== currentId) return;
      setError(err?.message ?? 'Falha ao carregar dados da dashboard');
      setData(null);
    } finally {
      if (fetchIdRef.current === currentId) {
        setLoading(false);
      }
    }
  }, []);

  // Carrega ao montar com fallback de 30 dias
  useEffect(() => {
    fetchData(defaults.start, defaults.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDateRange = useCallback(
    (start: Date, end: Date) => {
      setStartDate(start);
      setEndDate(end);
      fetchData(start, end);
    },
    [fetchData]
  );

  const setCurrentMonth = useCallback(() => {
    const now = new Date();
    setDateRange(startOfMonth(now), now);
  }, [setDateRange]);

  const setPreviousMonth = useCallback(() => {
    const lastMonth = subMonths(new Date(), 1);
    setDateRange(startOfMonth(lastMonth), endOfMonth(lastMonth));
  }, [setDateRange]);

  const setLast7Days = useCallback(() => {
    const today = new Date();
    setDateRange(subDays(today, 6), today);
  }, [setDateRange]);

  const setLast30Days = useCallback(() => {
    const today = new Date();
    setDateRange(subDays(today, 29), today);
  }, [setDateRange]);

  // Reprocessamento manual do período em exibição — estado isolado de loading/error
  // (que controlam os skeletons dos KPIs/gráficos) para não parecer que a dashboard
  // inteira travou/quebrou durante um reprocessamento.
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessError, setReprocessError] = useState<string | null>(null);
  const [reprocessResult, setReprocessResult] = useState<AnalyticsReprocessPeriodResult | null>(null);

  const reprocessPeriod = useCallback(async () => {
    setReprocessing(true);
    setReprocessError(null);
    setReprocessResult(null);
    try {
      const result = await analyticsReprocessPeriodService.run({
        startDate: toLocalDateString(startDate),
        endDate: toLocalDateString(endDate),
      });
      setReprocessResult(result);
      await fetchData(startDate, endDate); // reflete os agregados recém-recalculados
    } catch (err: any) {
      setReprocessError(err?.message ?? 'Falha ao reprocessar período');
    } finally {
      setReprocessing(false);
    }
  }, [startDate, endDate, fetchData]);

  return {
    data,
    loading,
    error,
    startDate,
    endDate,
    setDateRange,
    setCurrentMonth,
    setPreviousMonth,
    setLast7Days,
    setLast30Days,
    reprocessing,
    reprocessError,
    reprocessResult,
    reprocessPeriod,
  };
}
